import { useRef, useState } from "react"
import equal from "fast-deep-equal"
import { Alert, Box, Button, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import type { MessageContent, SlotOccurrence } from "../types"
import { safeDisplay, type EditorSlot } from "../editor/linkEditing"
import { UrlButtonDialog } from "./urlButtonDialog"
import { isUrlButtonStyle, urlButtonColors } from "./urlButtonStyle"

type Rows = MessageContent["buttons"]
interface RowKeys {
  key: number
  buttons: number[]
}
export interface TelegramUrlButtonsEditorProps {
  value: Rows
  onChange: (value: Rows) => void
  readOnly?: boolean
  keyboardAllowed?: boolean
  slots?: EditorSlot[]
  occurrences?: SlotOccurrence[]
  onSelectOccurrence?: (occurrence: SlotOccurrence) => void
  onCreateSlot?: () => string
}

export function TelegramUrlButtonsEditor({
  value,
  onChange,
  readOnly,
  keyboardAllowed = true,
  slots,
  occurrences,
  onSelectOccurrence,
  onCreateSlot,
}: TelegramUrlButtonsEditorProps) {
  const serial = useRef(0)
  const returnFocus = useRef<HTMLElement | null>(null)
  const keys = useRef<RowKeys[]>([])
  // Positional reconciliation for external replacements; owned moves carry keys.
  keys.current = value.map((row, index) => ({
    key: keys.current[index]?.key ?? serial.current++,
    buttons: row.map(
      (_, button) => keys.current[index]?.buttons[button] ?? serial.current++,
    ),
  }))
  const [dialog, setDialog] = useState<{
    row: number
    button?: number
    baseline: Rows
  } | null>(null)
  const disabled = Boolean(readOnly || !keyboardAllowed)
  const closeDialog = () => {
    setDialog(null)
    returnFocus.current?.focus()
  }
  const openDialog = (
    draft: NonNullable<typeof dialog>,
    target: HTMLElement,
  ) => {
    returnFocus.current = target
    setDialog(draft)
  }
  const update = (rows: Rows, nextKeys: RowKeys[]) => {
    if (readOnly) return
    keys.current = nextKeys
    onChange(rows)
  }
  const removeRow = (index: number) =>
    update(
      value.filter((_, i) => i !== index),
      keys.current.filter((_, i) => i !== index),
    )
  const moveRow = (from: number, to: number) => {
    const rows = [...value],
      nextKeys = [...keys.current]
    ;[rows[from], rows[to]] = [rows[to], rows[from]]
    ;[nextKeys[from], nextKeys[to]] = [nextKeys[to], nextKeys[from]]
    update(rows, nextKeys)
  }
  const changeRow = (
    row: number,
    buttons: Rows[number],
    buttonKeys: number[],
  ) => {
    if (!buttons.length) {
      removeRow(row)
      return
    }
    update(
      value.map((current, index) => (index === row ? buttons : current)),
      keys.current.map((current, index) =>
        index === row ? { ...current, buttons: buttonKeys } : current,
      ),
    )
  }
  const moveButton = (row: number, from: number, to: number) => {
    const buttons = [...value[row]],
      buttonKeys = [...keys.current[row].buttons]
    ;[buttons[from], buttons[to]] = [buttons[to], buttons[from]]
    ;[buttonKeys[from], buttonKeys[to]] = [buttonKeys[to], buttonKeys[from]]
    changeRow(row, buttons, buttonKeys)
  }
  const draft = dialog && (
    <UrlButtonDialog
      key={`${dialog.row}.${dialog.button ?? "new"}`}
      value={
        dialog.button === undefined
          ? undefined
          : dialog.baseline[dialog.row][dialog.button]
      }
      slots={slots}
      occurrences={occurrences}
      onSelectOccurrence={onSelectOccurrence}
      onCreateSlot={onCreateSlot}
      readOnly={disabled || !equal(dialog.baseline, value)}
      onClose={closeDialog}
      onSave={(button) => {
        if (disabled || !equal(dialog.baseline, value)) return
        const rows = value.map((row) => [...row]),
          nextKeys = keys.current.map((row) => ({
            ...row,
            buttons: [...row.buttons],
          }))
        if (dialog.row === rows.length) {
          rows.push([button])
          nextKeys.push({ key: serial.current++, buttons: [serial.current++] })
        } else if (dialog.button === undefined) {
          rows[dialog.row].push(button)
          nextKeys[dialog.row].buttons.push(serial.current++)
        } else rows[dialog.row][dialog.button] = button
        update(rows, nextKeys)
      }}
    />
  )
  return (
    <Stack spacing={1} sx={{ minWidth: 0 }} aria-label="URL-клавиатура">
      <Typography variant="subtitle2">URL-кнопки</Typography>
      {!keyboardAllowed && value.length > 0 && (
        <Alert severity="warning">У альбома не может быть кнопок</Alert>
      )}
      {value.map((row, rowIndex) => (
        <Box
          key={keys.current[rowIndex].key}
          role="group"
          aria-label={`Ряд кнопок ${rowIndex + 1}`}
          sx={{
            border: 1,
            borderColor: (theme) => alpha(theme.palette.text.primary, 0.14),
            borderRadius: 1,
            p: 1,
            minWidth: 0,
          }}
        >
          <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
            {row.map((button, buttonIndex) => (
              <Box
                key={keys.current[rowIndex].buttons[buttonIndex]}
                sx={{ minWidth: 0, maxWidth: "100%" }}
              >
                <Button
                  disabled={disabled}
                  data-telegram-button={`${rowIndex}.${buttonIndex}`}
                  aria-label={`Изменить кнопку ${safeDisplay(button.text)}`}
                  onClick={(event) =>
                    openDialog(
                      {
                        row: rowIndex,
                        button: buttonIndex,
                        baseline: value,
                      },
                      event.currentTarget,
                    )
                  }
                  sx={{
                    ...(isUrlButtonStyle(button.style) && {
                      backgroundColor: urlButtonColors[button.style],
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: urlButtonColors[button.style],
                        filter: "brightness(0.9)",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: urlButtonColors[button.style],
                        color: "#fff",
                        opacity: 0.65,
                      },
                    }),
                    overflowWrap: "anywhere",
                    "&:focus": {
                      outline: "2px solid",
                      outlineColor: "secondary.main",
                    },
                  }}
                >
                  {safeDisplay(button.text)}
                </Button>
                <Stack direction="row">
                  <Button
                    disabled={disabled || buttonIndex === 0}
                    aria-label={`Кнопка ${rowIndex + 1}.${buttonIndex + 1} влево`}
                    onClick={() =>
                      moveButton(rowIndex, buttonIndex, buttonIndex - 1)
                    }
                  >
                    ←
                  </Button>
                  <Button
                    disabled={disabled || buttonIndex === row.length - 1}
                    aria-label={`Кнопка ${rowIndex + 1}.${buttonIndex + 1} вправо`}
                    onClick={() =>
                      moveButton(rowIndex, buttonIndex, buttonIndex + 1)
                    }
                  >
                    →
                  </Button>
                  <Button
                    disabled={readOnly}
                    aria-label={`Удалить кнопку ${rowIndex + 1}.${buttonIndex + 1}`}
                    onClick={() =>
                      changeRow(
                        rowIndex,
                        row.filter((_, i) => i !== buttonIndex),
                        keys.current[rowIndex].buttons.filter(
                          (_, i) => i !== buttonIndex,
                        ),
                      )
                    }
                  >
                    ×
                  </Button>
                </Stack>
              </Box>
            ))}
          </Stack>
          <Stack direction="row" useFlexGap flexWrap="wrap">
            <Button
              disabled={disabled}
              onClick={(event) =>
                openDialog(
                  { row: rowIndex, baseline: value },
                  event.currentTarget,
                )
              }
            >
              Добавить кнопку в ряд {rowIndex + 1}
            </Button>
            <Button
              disabled={disabled || rowIndex === 0}
              aria-label={`Ряд ${rowIndex + 1} вверх`}
              onClick={() => moveRow(rowIndex, rowIndex - 1)}
            >
              ↑
            </Button>
            <Button
              disabled={disabled || rowIndex === value.length - 1}
              aria-label={`Ряд ${rowIndex + 1} вниз`}
              onClick={() => moveRow(rowIndex, rowIndex + 1)}
            >
              ↓
            </Button>
            <Button disabled={readOnly} onClick={() => removeRow(rowIndex)}>
              Удалить ряд {rowIndex + 1}
            </Button>
          </Stack>
          {dialog?.row === rowIndex && draft}
        </Box>
      ))}
      <Stack direction="row" useFlexGap flexWrap="wrap">
        <Button
          disabled={disabled}
          onClick={(event) =>
            openDialog(
              { row: value.length, baseline: value },
              event.currentTarget,
            )
          }
        >
          Добавить ряд
        </Button>
        {!!value.length && (
          <Button
            disabled={readOnly}
            onClick={() => {
              if (!readOnly) {
                keys.current = []
                onChange([])
              }
            }}
          >
            Удалить все кнопки
          </Button>
        )}
      </Stack>
      {dialog && dialog.row >= value.length && draft}
    </Stack>
  )
}
